/**
 * IncomingCallAlert — Alert bei eingehendem Kundenanruf (Paket 2310d).
 *
 * Sichtbar nur für Admins mit der Berechtigung `3cx.anrufe.anzeigen`. Pollt
 * GET /v2/admin/3cx/incoming-calls (4 s). Quelle im Backend ist der lookup-
 * Webhook von 3CX, weil ReportCall (und damit der active-call-Poll des
 * CallPopup) erst nach Gesprächsende feuert. Der Alert gilt für alle
 * Berechtigten, nicht nur für die angerufene Extension.
 *
 * Zeigt Profilbild, Name, Button "Profil öffnen" und ein X. Das Wegklicken wird
 * je Anruf (Ring-ID) in sessionStorage gemerkt, ein neuer Anruf zeigt wieder an.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { v2ThreeCx, type ThreeCxIncomingCall } from '../../lib/threeCx';
import { getThemeColors, BRAND, BRAND_LIGHT } from '../../lib/theme';
import { Icon } from '../icons';
import { Avatar } from '../ui';
import { AppLink } from '../../lib/openLink';

export const INCOMING_CALL_PERMISSION = '3cx.anrufe.anzeigen';
const POLL_INTERVAL_MS = 4000;
const STORAGE_KEY = 'sh24.incomingCall.dismissed';

function loadDismissed(): number[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((n): n is number => typeof n === 'number') : [];
  } catch {
    return [];
  }
}

function saveDismissed(ids: number[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-50)));
  } catch {
    /* Storage nicht verfügbar: Wegklicken gilt dann nur bis zum nächsten Reload */
  }
}

export function IncomingCallAlert({ dark }: { dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();

  const [calls, setCalls] = useState<ThreeCxIncomingCall[]>([]);
  const [dismissed, setDismissed] = useState<number[]>(loadDismissed);

  const shouldPoll = isAuthenticated && hasPermission(INCOMING_CALL_PERMISSION);

  useEffect(() => {
    if (!shouldPoll) {
      setCalls([]);
      return;
    }
    let alive = true;
    const poll = async () => {
      try {
        const list = await v2ThreeCx.incomingCalls();
        if (alive) setCalls(list);
      } catch {
        /* stiller Fehlschlag: nächster Tick versucht es erneut */
      }
    };
    void poll();
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => { alive = false; window.clearInterval(interval); };
  }, [shouldPoll]);

  const visible = calls.filter(c => !dismissed.includes(c.id));
  if (!shouldPoll || visible.length === 0) return null;

  const dismiss = (id: number) => {
    const next = [...dismissed, id];
    setDismissed(next);
    saveDismissed(next);
  };

  /** Pfad des Profil-Links (nur Pfad, gleiche Origin); null bei ungültiger URL. */
  const profileHref = (c: ThreeCxIncomingCall): string | null => {
    if (!c.profile_url) return null;
    try {
      const u = new URL(c.profile_url, window.location.origin);
      return u.pathname + u.search + u.hash;
    } catch {
      return null;
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 72, right: 20, zIndex: 1100,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {visible.map(c => {
        const name = c.contact_name ?? c.external_number ?? tr('incoming_call_alert.unknown_caller');
        return (
          <div
            key={c.id}
            role="alert"
            data-testid="incoming-call-alert"
            className="fade-in"
            style={{
              width: 320, borderRadius: 14, overflow: 'hidden',
              background: t.card, border: `1px solid ${BRAND}`,
              boxShadow: '0 12px 32px -8px rgba(16,24,40,0.3)',
            }}
          >
            <div style={{
              padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8,
              background: BRAND_LIGHT, borderBottom: `1px solid ${t.borderSoft}`,
            }}>
              <Icon.Phone c={BRAND} />
              <span style={{ flex: 1, fontSize: 11.5, fontWeight: 600, color: BRAND }}>
                {tr('incoming_call_alert.title')}
              </span>
              <button
                onClick={() => dismiss(c.id)}
                aria-label={tr('incoming_call_alert.close')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 2 }}
              >
                <Icon.Close c={BRAND} size={14} />
              </button>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: c.profile_url ? 12 : 0 }}>
                <Avatar name={name} size={48} src={c.avatar_url} userId={c.avatar_url ? null : c.user_id} dark={dark} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </div>
                  <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>{c.external_number}</div>
                </div>
              </div>
              {profileHref(c) && (
                <AppLink
                  to={profileHref(c) as string}
                  style={{
                    display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box',
                    width: '100%', fontSize: 12.5, fontWeight: 600,
                    background: BRAND, color: '#fff', border: `1px solid ${BRAND}`,
                    borderRadius: 8, padding: '7px 0', cursor: 'pointer',
                  }}
                >
                  {tr('incoming_call_alert.open_profile')}
                </AppLink>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
